/* eslint-disable max-len */
import { PaymentCollectionType } from '@features/group-management/constants';
import { MemberStatus } from '@features/group-management/types';

import { ApiError } from './api';
import type { Member, MemberTransactionHistoryItem } from './member';
import { ValidationError } from './requests';
import { TabPill } from './tabs';
import type { PaymentOrderAmountType } from './transaction';

export interface Group {
  amount: string;
  id: string;
  image_url?: string;
  interval: string;
  intervalFrequency: number;
  is_active: boolean;
  is_payment_link_sent: boolean;
  is_split_payment: boolean;
  member_count: number;
  active_member_count: number;
  name: string;
  pay_due: string;
  pay_received: string;
  pending_count: number;
  end_date?: string;
  is_attendance_based_fee_enabled?: boolean;
}

export interface GroupInfoOverviewTabProps {
  monthFilter: number;
  setMonthFilter: (value: number) => void;
  paymentList: any;
  collectedValue: number;
  dueValue: number;
  collectionStartedAt: string;
  paymentLinkGeneratedOn: string;
  groupDetails: GroupDetailsType;
  setGroupDetails: (groupDetails: GroupDetailsType) => void;
  selectedTab: TabPill;
  setSelectedTab: (tab: TabPill) => void;
  tabs: TabPill[];
  hasGroupAttendanceViewAccess: boolean;
  setIsConfirmationModalOpen: (value: boolean) => void;
  setShouldActivateGroup: (value: boolean) => void;
}

export enum InsightCardType {
  COLLECTED = 'collected',
  DUE = 'due'
}

export interface InsightCardProps {
  type: InsightCardType;
  value: number;
  className?: string;
}

export interface GroupInfoCardProps {
  groupDetails: GroupDetailsType;
  isActive: boolean;
  handleGroupStatusChange: (status: boolean) => void;
  isLoading?: boolean;
  isEditGroupOpen: boolean;
  setIsEditGroupOpen: (open: boolean) => void;
  hasGroupEditAccess?: boolean;
  hasGroupDisableAccess?: boolean;
  isAttendanceManager: boolean;
  defaultTermLabel?: string;
}

export interface FeeConfigurationType {
  branch_fee_category_id: number;
  account_detail_id: number;
  category: string;
  amount: string;
  isManuallyUpdated?: boolean;
}

export interface MemberDetailsProps {
  member: Member;
  handleDelete: (sendPaymentLink?: boolean) => void;
  handleMemberStatusUpdateSuccess: (status: MemberStatus) => void;
  handleMemberStatusUpdateError: (
    error: ApiError,
    status: MemberStatus
  ) => void;
  handleMemberUpdateSuccess: (member: MemberListType) => void;
  handleMemberUpdateError: (error: ApiError, member: MemberListType) => void;
  paymentCollectionData: {
    type: PaymentCollectionType | '';
    intervalFrequency: number;
  };
  getApiParams: () => {
    organizationId: string;
    branchId: string;
    groupId: string;
  };

  hasGroupMemberEditAccess?: boolean;
  isAttendanceTrackingEnabled: boolean;
  isAttendanceMarked: boolean;
  isFeeConfigured?: boolean;
  feeConfigurationDetails?: FeeConfigurationType[];
  groupFrequency: string;
  groupIntervalFrequency: number;
  isImmediatePayment?: boolean;
  groupEndDate?: string;
  isAttendanceBasedFeeEnabled?: boolean;
  isSessionBased?: boolean;
  sessionsPerCycle?: number;
  defaultTermLabel?: string;
}

export interface MonthArrayType {
  id: number;
  label: string;
}

export interface InsightProps {
  filterValue: number;
  setFilterValue: (val: number) => void;
  collectedValue: number;
  dueValue: number;
  collectionStartedAt: string;
  yearFilter?: number;
}

export type BulkPaymentDiscountMetric = {
  type: string;
  value: string | number;
};

export type BulkPaymentDiscountType = {
  ordering_key?: number;
  name?: string;
  threshold: BulkPaymentDiscountMetric;
  discount: BulkPaymentDiscountMetric;
};

export type FormDataType = {
  groupPhoto: any;
  groupName: string;
  activationDate: string;
  intervalFrequency: string;
  amount: string;
  paymentInstallment: boolean;
  paymentCollectionType: PaymentCollectionType | '';
  endDate?: string;
  startDate?: string;
  isAttandanceTrackingEnabled?: boolean;
  isSendAttendanceAlertEnabled?: boolean;
  workingDays?: string[];
  isPaymentLinkSent?: boolean;
  collectionStartedAt?: string | null;
  isCollectionStarted?: boolean;
  isImmediatePayment?: boolean;
  isCustomTerm?: boolean;
  dateFrequency?: string;
  nextDueAt?: string;
  dueDateConsent?: DueDateOptions;
  termsList?: TermsList[];
  isAttendanceBasedFee?: boolean;
  sessionsPerCycle?: number;
  feeCategoryEnabled?: boolean;
  isAttendanceBasedFeeEnabled?: boolean;
  isBulkPaymentDiscountEnabled?: boolean;
  bulkPaymentDiscountData?: BulkPaymentDiscountType[];
};

export type FeeCategoryDataType = {
  id: number;
  label: string;
  value: string;
  isManuallyUpdated?: boolean;
};

export type AddMemberFormDataType = {
  feeCategories: FeeCategoryDataType[];
  termsList: TermsList[];
  name: string;
  email: string;
  primaryCountryCode: string;
  primaryNumber: string;
  altCountryCode: string;
  altNumber: string;
  amount: string;
  dueDate: {
    day: string;
    paymentCollectionType: PaymentCollectionType | '';
    interval: string;
  };
  startDate: string;
  endDate: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  totalFee: string;
  sessionsPerCycle: number;
  dateOfBirth: string;
  bloodGroup: string;
  guardianName: string;
  admissionNumber: string;
  admissionDate: string;
  counsellorName: string;
  isInsured: string;
  discount?: {
    isEnabled: boolean;
    label: string;
    value: string;
  };
};

export type MemberListType = {
  altNumber?: string;
  amount: string;
  dueDate: string;
  frequency?: string;
  monthsPerTerm?: string;
  startDate?: string;
  endDate?: string;
  name: string;
  primaryNumber: string;
  id: string;
  is_payment_link_sent?: boolean;
  is_fee_category_enabled?: boolean;
  feeCategoryData?: FeeCategoryDataType[];
  fee_catagoryInfo_for_edit?: FeeConfigurationType[];
  primaryNumberCountryCode?: string;
  altNumberCountryCode?: string;
  email?: string;
  amountPaid?: string;
  admissionNumber?: string;
  admissionDate?: string;
  dateOfBirth?: string;
  guardianName?: string;
  bloodGroup?: string;
  counsellorName?: string;
  isInsured?: string;
  age?: string;
  paymentCollectionType?: PaymentCollectionType | '';
  interval?: string;
  totalFee?: string;
  paymentSchedule?: {
    startDate: string;
    endDate: string;
    intervalFrequency: number;
    interval: string;
    monthlyConfig: {
      dayOption: string;
      customDay: number;
    };
    weeklyConfig: {
      dayOfWeek: string;
    };
  };
  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;
  pincode?: string;
  skip_current_interval?: boolean;
  is_collection_started?: boolean;
  // termList?: TermsList[];
  termsList?: TermsList[];
  sessionsPerCycle?: number;
  group_fee_amount_configuration?: FeeConfigurationType[];
  mandate_max_amount?: string;
  is_mandate_enabled?: boolean;
  discount?: MemberDiscountLine[];
  formSubmissionId?: string;
};

export type MemberDiscountLine = {
  label: string;
  type: string;
  value?: string;
  applied_on?: string;
  amount: string;
};

export type TermsList = {
  id: string;
  term_label: string;
  status?: string;
  dueDate: string;
  dueAmount: string;
  feeCategoryData?: FeeConfigurationType[];
  isPaymentLinkSent?: boolean;
  originalAmountRequestedDetails?: PaymentOrderAmountType;
  applied_group_member_discount_details?: MemberDiscountLine[];
  term_number?: number;
};

export type MemberTermPaymentDetails = {
  paid_on: string | null;
  paid_amount: string;
  pending_amount: string;
  payment_status: string;
  payment_order_id: string;
  disabled_at?: string | null;
  payment_mode?: string;
  payment_method?: string;
};

export type TermsConfig = {
  term_number: number;
  term_label?: string;
  label?: string;
  term_duration?: number;
  term_start_date?: string;
  term_end_date?: string;
  due_date: string;
  fee_amount: string;
  status?: string;
  fee_split?:
    | {
        branch_fee_category_id: number;
        account_detail_id: number;
        category: string;
        amount: string;
        base_amount?: string;
      }[]
    | null;
  is_payment_link_sent?: boolean;
  payment_details?: MemberTermPaymentDetails;
  applied_group_member_discount_details?: MemberDiscountLine[];
  original_amount_requested_details?: PaymentOrderAmountType;
  transaction_history?: MemberTransactionHistoryItem[];
};

export type GroupDetailsType = {
  id: string;
  name: string;
  amount: string;
  group_total_amount?: string;
  frequency: string;
  interval_frequency: number;
  is_recurring: boolean;
  is_active: boolean;
  activation_date?: string;
  member_count: number;
  active_member_count: number;
  pay_received?: string;
  pay_due?: string;
  allow_activation_date_update?: boolean;
  payment_list?: any[];
  is_payment_link_sent?: boolean;
  is_split_payment?: boolean;
  next_due_at: string;
  remind_again?: boolean;
  collection_started_at?: string;
  image_url?: string;
  created_at: string;
  end_date: string;
  start_date?: string;
  is_attendance_tracking_enabled?: boolean;
  is_send_attendance_alert_enabled?: boolean;
  is_attendance_marked?: boolean;
  work_days?: string[];
  is_fee_configured: boolean;
  group_fee_amount_configuration?: FeeConfigurationType[];
  interval_execution_type?: string;
  custom_members?: {
    name: string;
    country_code: string;
    mobile: string;
  }[];
  payment_schedule?: {
    start_date?: string;
    end_date?: string | null;
    sessions_per_cycle?: number;
    interval_frequency?: number;
    interval?: string;
    interval_execution_type?: string;
    monthly_config?: {
      dayOption: string;
      customDay: number;
    };
    weekly_config?: {
      dayOfWeek: string;
    };
    term_details?: TermsConfig[];
    custom_amount_minimum?: number | null;
    bulk_payment_discount_details?: BulkPaymentDiscountType[] | null;
  };
  attendance_config?: {
    is_attendance_tracking_enabled: boolean;
    is_attendance_based_fee_enabled: boolean;
    is_send_attendance_alert_enabled: boolean;
    working_days: string[];
  };
  is_attendance_based_fee_enabled?: boolean;
  custom_amount_minimum?: string;
};

export interface AddMembersProps {
  memberList: MemberListType[];
  handleAddManually: (item: MemberListType) => void;
  handleAddViaFile: (memberList: MemberListType[]) => void;
  handleAddMembersFromList: (memberList: MemberListType[]) => void;
  paymentCollectionType: PaymentCollectionType | '';
  setMemberList: (memberList: any) => void;
  handleEdit: (memberId: string) => void;
  handleDelete: (memberId: string) => void;
  groupAmount: string;
  groupDueDate?: string;
  groupFrequency?: number;
  selectedTab: TabPill;
  setSelectedTab: (tab: TabPill) => void;
  memberFile: File | undefined;
  setMemberFile: (file: File | undefined) => void;
  isCategoryEnabled?: boolean;
  feeCategoryData?: {
    id: number;
    category: string;
    account_detail_id: number;
  }[];
  feeCategories?: FeeCategoryDataType[];
  groupStartDate?: string;
  isImmediatePayment: boolean;
  isCustomTerm: boolean;
  selectedRows: string[];
  setSelectedRows: (data: string[]) => void;
  termsList?: TermsList[];
}

export type GroupFeeConfigurations = {
  id?: string | number;
  category: string;
  amount?: string;
  account_detail_id?: string | number;
  branch_fee_category_id?: string | number;
};

export type TermData = {
  term_number: number;
  label?: string;
  due_date: string;
  fee_amount: string;
  fee_split: {
    branch_fee_category_id: number;
    amount: string;
    category: string;
    account_detail_id: number;
    base_amount?: string;
  }[];
  is_payment_link_sent: boolean;
};

export interface ImportFileTabProps {
  handleAdd: (memberList: any) => void;
  groupAmount?: string;
  groupDueDate?: string;
  memberFile?: File | undefined;
  setMemberFile?: (file: File | undefined) => void;
  groupFeeConfigurations?: GroupFeeConfigurations[];
  isOnce: boolean;
  groupFrequency?: string;
  groupIntervalFrequency?: number;
  isCustomTerm?: boolean;
  // termsData?: {
  //   name: string;
  //   amount: string;
  //   feeInfo: {
  //     category: string;
  //     amount: string;
  //   }[];
  // }[];
  termsData?: TermData[];
  groupStartDate?: string;
  groupEndDate?: string;
  isImmediatePayment?: boolean;
  isFeeCategoryEnabled?: boolean;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  sessionsPerCycle?: number;
  activeGroupId?: string;
  defaultTermLabel?: string;
}

export interface AddManuallyTabProps {
  handleAdd: () => void;
  columnHeaderList: { Header: string; accessor: string; style?: string }[];
  memberList: MemberListType[];
  handleDelete: (memberId: string) => void;
  handleEdit: (memberId: string) => void;
  maxTableHeight?: string;
  infiniteScrollRequired?: boolean;
  setPage?: (prevPage: any) => void;
  isLoading?: boolean;
  disablePagination?: boolean;
  groupAmount?: string;
  groupDueDate?: string;
  groupFrequency?: number;
  isImmediatePayment?: boolean;
  isCustomTerm?: boolean;
  groupStartDate?: string;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  groupSessionsPerCycle?: number;
  defaultTermLabel?: string;
}

export interface CustomTermTableProps {
  termsList: TermsList[];
  setTermsList: (
    termsList: TermsList[],
    redistributeFromHeader?: boolean
  ) => void;
  isFeeCategoryEnabled: boolean;
  feeCategories: FeeCategoryDataType[];
  onAmountChange: (value: any) => void;
  isMember?: boolean;
  isEditGroup?: boolean;
  memberFlow?: 'add' | 'edit';
  error?: ValidationError;
  setError?: (error: ValidationError) => void;
  groupEndDateValidationMessage?: string;
  splitHeaderAmountToTerms?: boolean;
  onEndSplitHeaderAmountMode?: () => void;
  onStartSplitHeaderAmountMode?: () => void;
  defaultTermLabel?: string;
  className?: string;
  memberDiscounts?: MemberDiscountLine[];
}

export enum AddMemberModalType {
  ADD = 'add',
  EDIT = 'edit'
}

export type FeeCategoryValueDataType = {
  id: number;
  category: string;
  account_detail_id: number;
  value?: string;
};

export interface MemberValidationResult {
  hasError: boolean;
  hasWarning: boolean;
  errorMessage?: string;
  warningMessage?: string;
  admissionNumber?: string;
}

export interface AddMemberProps {
  type?: AddMemberModalType;
  isOpen: boolean;
  handleCancel: () => void;
  addMember: (data: MemberListType) => void;
  paymentCollectionType: PaymentCollectionType | '';
  selectedmember?: MemberListType | null;
  groupAmount?: string;
  groupDueDate?: string;
  groupFrequency?: number;
  isFeeCategoryEnabled?: boolean;
  feeCategoryData?: FeeCategoryValueDataType[];
  feeCategoriesValues?: FeeCategoryDataType[];
  memberList: MemberListType[];
  groupStartDate?: string;
  isImmediatePayment?: boolean;
  isCustomTerm?: boolean;
  termsList?: TermsList[];
  groupEndDate?: string;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  groupSessionPerCycle?: number;
  // Optional v3 API validation for group context
  validateMember?: (data: MemberListType) => Promise<MemberValidationResult>;
  isValidationLoading?: boolean;
  defaultTermLabel?: string;
}

export type EditGroupType = {
  groupPhoto: { file?: any; preview?: any; image_url?: string };
  name: string;
  amount: string;
  next_due_at: string;
  frequency: string;
  interval_frequency: number;
  paymentInstallment?: boolean;
  end_date: string;
  isAttandanceTrackingEnabled?: boolean;
  isSendAttendanceAlertEnabled?: boolean;
  workingDays?: string[];
};

export interface EditGroupProps {
  isOpen: boolean;
  handleCancel: () => void;
  groupDetails: GroupDetailsType;
  onSaveEditGroup: (data: EditGroupType) => void;
}

export enum SectionHeaders {
  BASICINFO = 'basicInfo',
  ADDRESS = 'address',
  GROUP_DETAILS = 'groupDetails',
  PERSONAL_INFO = 'personalInfo',
  MORE_INFO = 'moreInfo',
  CUSTOM_FIELDS = 'customFields'
}

export type SectionHeadersType =
  | SectionHeaders.BASICINFO
  | SectionHeaders.ADDRESS
  | SectionHeaders.GROUP_DETAILS
  | SectionHeaders.PERSONAL_INFO
  | SectionHeaders.MORE_INFO
  | SectionHeaders.CUSTOM_FIELDS;

export type DueDateOptions = 'next' | 'current';
